import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { usePermiso } from '../sesion/ProveedorDeSesion';

/**
 * La lista de precios asignada a una sucursal o a un puesto de caja, elegida en la misma fila
 * (E36). Guarda al cambiar: es un solo dato, y abrir un diálogo para un desplegable sería más
 * trabajo que el que ahorra.
 *
 * «La que le toque» es no tener una propia: la venta sigue con la siguiente de la cadena.
 */
export function ListaAsignada({
  valor,
  ruta,
  clave,
  puedeEditar,
}: {
  readonly valor: string | null;
  /** La ruta PATCH de la fila: `/sucursales/…` o `/puestos-de-caja/…`. */
  readonly ruta: string;
  /** La consulta a refrescar después de guardar. */
  readonly clave: string;
  readonly puedeEditar: boolean;
}): React.ReactElement | null {
  const { t } = useTranslation();
  const cola = useQueryClient();
  const puedeVerListas = usePermiso('PRECIOS.LISTA.VER');
  const listas = useQuery({
    queryKey: ['listas-de-precios'],
    enabled: puedeVerListas,
    queryFn: () => pedir<{ id: string; nombre: string }[]>('/listas-de-precios'),
  });
  const guardar = useMutation({
    mutationFn: (listaDePreciosId: string | null) =>
      pedir<void>(ruta, { method: 'PATCH', cuerpo: { listaDePreciosId } }),
    onSuccess: async () => {
      await cola.invalidateQueries({ queryKey: [clave] });
    },
  });

  if (!puedeVerListas) return null;
  return (
    <select
      value={valor ?? ''}
      disabled={!puedeEditar || guardar.isPending}
      aria-label={t('listasDePrecios.asignada')}
      onChange={(evento) => {
        guardar.mutate(evento.target.value === '' ? null : evento.target.value);
      }}
      className="rounded-marco border border-borde bg-transparent px-2 py-1 text-sm disabled:opacity-50"
    >
      <option value="">{t('ficha.sinListaPropia')}</option>
      {(listas.data ?? []).map((lista) => (
        <option key={lista.id} value={lista.id}>
          {lista.nombre}
        </option>
      ))}
    </select>
  );
}
